import {
  Box,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  useOutsideClick,
} from '@chakra-ui/react';
import { Collapse } from 'react-collapse';
import { useDispatch, useSelector } from 'react-redux';
import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { VariableSizeList as List } from 'react-window';

import InputField from 'components/InputField';
import Spinner from 'components/Spinner';
import {
  PostDeleteDimensionItem,
  UpdateItemName,
  fetchDimensionItemsGroup,
  getDataIndicators,
  getDisplayInfo,
  getItemId,
  getOpenItem,
  getRenameItemName,
  setDisplayInfo,
  setIsItemRenamed,
  setRenameItemName,
  setopenItem,
  setSelectedElement,
} from 'redux/ItemPageSlice';
import { FetchBlockItemDimensions, getSelectedDriver, handleFilters } from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import DeleteModal from 'components/Modals/DeleteModal';
import { AppDispatch } from 'utils/GlobalHelpers';
import apiService from 'services';

import useClickAndDoubleClickHandler from '../../../../hooks/useClickAndDoubleClickHandler';
import arrowDown from '../Icons/arrowDown.svg';
import arrowUp from '../Icons/arrowUp.svg';
import infoIcon from '../Icons/infoIcon.svg';

import DatePickerDrivers from './DatePickerDrivers';
import DesktopProperties from './DesktopProperties';

interface DesktopElementsProperties {
  _index: number;
  dataKey: any;
  styles: any;
  onSubmitValue: (value: string, id: number, propertyValue: string) => void;
  longClickCheck: any;
  isLongClicked: boolean;
  planPermission?: boolean;
}

const DesktopElements = ({
  _index,
  dataKey,
  styles,
  onSubmitValue,
  longClickCheck,
  isLongClicked,
  planPermission,
}: DesktopElementsProperties) => {
  const { blockId } = useParams();
  const [itemNameField, setItemNameField] = useState<Record<number, string>>({});
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [DeleteIdModal, setDeleteIdModal]: any = useState(null);
  const [DeleteNameModal, setDeleteNameModal] = useState('');
  const [isRenaming, setIsRenaming] = useState<Record<number, boolean>>({});
  const [renameSuccess, setRenameSuccess] = useState<Record<number, boolean>>({});
  const dispatch: AppDispatch = useDispatch();
  const dataIndicators = useSelector(getDataIndicators);
  const displayInfo = useSelector(getDisplayInfo);
  const currentDriver = useSelector(getSelectedDriver);
  const renameItemName = useSelector(getRenameItemName);
  const show = useSelector(getOpenItem);

  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const baseScenario = useSelector(getBaseScenario);
  const debounceTimeoutReference = useRef<NodeJS.Timeout | null>(null);
  const isApiCallInProgressReference = useRef<Record<number, boolean>>({});
  const itemId = useSelector(getItemId);
  const [connectedDimensionItemsMap, setConnectedDimensionItemsMap] = useState<
    Record<number, any[]>
  >({});
  const hasUserTypedReference = useRef<Record<number, boolean>>({});
  const listReference = useRef<List>(null);
  const listContainerReference = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState(600);
  const itemHeightsReference = useRef<Record<number, number>>({});
  const itemReferencesReference = useRef<Record<number, HTMLDivElement | null>>({});
  const measurementTimeoutsReference = useRef<Record<number, NodeJS.Timeout>>({});
  const VIRTUALIZATION_THRESHOLD = 50; // Only virtualize when items exceed this count

  const handleShow = (id: number) => {
    if (show === id) {
      return dispatch(setopenItem(null));
    }
    return dispatch(setopenItem(id));
  };

  const renameItem = (event: any, id: number) => {
    event.stopPropagation();
    dispatch(setDisplayInfo(null));
    if (renameItemName === id) {
      return dispatch(setRenameItemName(0));
    }
    return dispatch(setRenameItemName(id));
  };

  useOutsideClick({
    ref: reference,
    handler: () => {
      dispatch(setDisplayInfo(null));
    },
  });

  const toggleInfoPopup = (id: number | null) => {
    if (displayInfo === id) {
      return dispatch(setDisplayInfo(null));
    }
    return dispatch(setDisplayInfo(id));
  };

  const showOrRename = useClickAndDoubleClickHandler(handleShow, renameItem);

  const UpdateItemApi = useCallback(
    async (item_id: number, name: string) => {
      // Prevent multiple simultaneous calls for the same item
      if (isApiCallInProgressReference.current[item_id]) {
        return;
      }

      // Check if name is empty or unchanged
      if (!name || !name.trim()) {
        return;
      }

      // Set loading state
      setIsRenaming((previous) => ({ ...previous, [item_id]: true }));
      setRenameSuccess((previous) => ({ ...previous, [item_id]: false }));
      isApiCallInProgressReference.current[item_id] = true;

      try {
        const requestBody = { name: name.trim(), scenario_id: baseScenario?.id };
        await dispatch(
          UpdateItemName({
            item_id,
            request_body: requestBody,
            toaster: {
              successMessage: 'Successfully updated',
              errorMessage: 'An item with this name already exists',
            },
          }),
        );
        await dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
        await dispatch(
          FetchBlockItemDimensions({
            blockId,
          }),
        );
        await dispatch(handleFilters());

        // Set success state
        setRenameSuccess((previous) => ({ ...previous, [item_id]: true }));
        setIsItemRenamed(true);
        setItemNameField((previous) => {
          const updated = { ...previous };
          delete updated[item_id];
          return updated;
        });

        // Clear success state after a short delay
        setTimeout(() => {
          setRenameSuccess((previous) => ({ ...previous, [item_id]: false }));
        }, 2000);
      } catch {
        // Error is handled by the toaster in the action
        // Silently handle error as it's already shown via toaster
      } finally {
        setIsRenaming((previous) => ({ ...previous, [item_id]: false }));
        isApiCallInProgressReference.current[item_id] = false;
      }
    },
    [dispatch, baseScenario, currentDriver, blockId],
  );

  // Debounced version of UpdateItemApi
  const debouncedUpdateItemApi = useCallback(
    debounce((item_id: number, name: string) => {
      UpdateItemApi(item_id, name);
    }, 500),
    [UpdateItemApi],
  );

  const onKeyPressAction = async (event: any, item_id: number) => {
    if (event.code === 'Enter') {
      // Clear any pending debounced calls
      if (debounceTimeoutReference.current) {
        clearTimeout(debounceTimeoutReference.current);
        debounceTimeoutReference.current = null;
      }
      // Cancel debounce and call immediately
      debouncedUpdateItemApi.cancel();
      const name = event.target.value || itemNameField[item_id] || '';
      if (name && name.trim().length > 0) {
        await UpdateItemApi(item_id, name);
      }
    }
    if (event.code === 'Escape') {
      // Clear any pending debounced calls
      if (debounceTimeoutReference.current) {
        clearTimeout(debounceTimeoutReference.current);
        debounceTimeoutReference.current = null;
      }
      debouncedUpdateItemApi.cancel();
      setItemNameField((previous) => {
        const updated = { ...previous };
        delete updated[item_id];
        return updated;
      });
      setIsRenaming((previous) => ({ ...previous, [item_id]: false }));
      dispatch(setRenameItemName(0));
    }
  };

  // Cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimeoutReference.current) {
        clearTimeout(debounceTimeoutReference.current);
      }
      debouncedUpdateItemApi.cancel();
    };
  }, [debouncedUpdateItemApi]);

  // Collect all connected dimension IDs from all properties
  const findAllConnectedDimensions = useCallback(() => {
    if (!itemId?.properties) return [];
    const dimensionIds = new Set<number>();
    itemId.properties.forEach((property: any) => {
      if (property.data_format && property.type === 'dimension') {
        try {
          const dataFormat = JSON.parse(property.data_format || '{}');
          if (dataFormat?.dimension) {
            // Convert to number if it's a string (API returns dimension IDs as strings in JSON)
            const dimensionId = Number(dataFormat.dimension);
            if (!Number.isNaN(dimensionId)) {
              dimensionIds.add(dimensionId);
            }
          }
        } catch {
          // Invalid JSON, skip
        }
      }
    });
    return Array.from(dimensionIds);
  }, [itemId]);

  // Fetch all connected dimension items in bulk
  React.useEffect(() => {
    const connectedDimensionIds = findAllConnectedDimensions();
    if (connectedDimensionIds.length > 0 && baseScenario?.id) {
      const dimensionIdsString = connectedDimensionIds.join(',');
      apiService
        .get_dimension_items_bulk({
          params: {
            dimension_ids: dimensionIdsString,
            scenario_id: baseScenario.id,
            onlyitems: false,
          },
        })
        .then((response: any) => {
          // The API returns items grouped by dimension when onlyitems=false
          // Response structure: { data: { dimensions: [{ dimension_id, dimension_name, items }, ...] } }
          const itemsMap: Record<number, any[]> = {};

          // Check for response.data.dimensions first (actual API structure)
          const dimensionsArray =
            response.data?.dimensions || response.dimensions || response.data || response;

          if (Array.isArray(dimensionsArray)) {
            // Handle array of dimension objects: [{ dimension_id, dimension_name, items }, ...]
            dimensionsArray.forEach((dimensionData: any) => {
              if (dimensionData?.dimension_id && Array.isArray(dimensionData.items)) {
                const dimId = Number(dimensionData.dimension_id);
                if (!Number.isNaN(dimId)) {
                  itemsMap[dimId] = dimensionData.items;
                }
              }
            });
          } else if (
            typeof dimensionsArray === 'object' &&
            dimensionsArray !== null &&
            dimensionsArray.dimension_id &&
            Array.isArray(dimensionsArray.items)
          ) {
            // Handle single dimension object: { dimension_id, dimension_name, items }
            const dimId = Number(dimensionsArray.dimension_id);
            if (!Number.isNaN(dimId)) {
              itemsMap[dimId] = dimensionsArray.items;
            }
          } else if (typeof dimensionsArray === 'object' && dimensionsArray !== null) {
            // Handle object structure keyed by dimension ID
            Object.keys(dimensionsArray).forEach((key) => {
              const dimensionData = dimensionsArray[key];
              let dimId: number | null = null;
              let items: any[] | null = null;

              // Check if key is a dimension ID (numeric string)
              const keyAsNumber = Number.parseInt(key, 10);
              if (!Number.isNaN(keyAsNumber)) {
                dimId = keyAsNumber;
                // Check if the value is an array (direct items) or an object with items
                if (Array.isArray(dimensionData)) {
                  items = dimensionData;
                } else if (dimensionData?.items && Array.isArray(dimensionData.items)) {
                  items = dimensionData.items;
                } else if (dimensionData?.dimension_id) {
                  // If it's an object with dimension_id, use that instead of the key
                  dimId = Number(dimensionData.dimension_id);
                  items = Array.isArray(dimensionData.items) ? dimensionData.items : null;
                }
              } else if (dimensionData?.dimension_id) {
                // Key is not numeric, but object has dimension_id
                dimId = Number(dimensionData.dimension_id);
                items = Array.isArray(dimensionData.items) ? dimensionData.items : null;
              }

              if (dimId && !Number.isNaN(dimId) && items) {
                itemsMap[dimId] = items;
              }
            });
          }

          setConnectedDimensionItemsMap(itemsMap);
        })
        .catch(() => {
          // Error fetching bulk dimension items, set empty map
          setConnectedDimensionItemsMap({});
        });
    } else {
      setConnectedDimensionItemsMap({});
    }
  }, [findAllConnectedDimensions, baseScenario?.id]);

  const getDisplayProperties = (originalProperties: any) => {
    const properties = JSON.parse(JSON.stringify(originalProperties));
    const startDateKey = Object.keys(properties).find((key) => {
      if (
        `${key}`.toLowerCase().trim() === 'start date' ||
        `${key}`.toLowerCase().trim() === 'start'
      ) {
        return key;
      }
      return false;
    });

    const endDateKey = Object.keys(properties).find((key) => {
      if (`${key}`.toLowerCase().trim() === 'end date' || `${key}`.toLowerCase().trim() === 'end') {
        return key;
      }
      return false;
    });
    if (startDateKey) {
      delete properties[startDateKey];
    }
    if (endDateKey) {
      delete properties[endDateKey];
    }
    return properties;
  };

  const DeleteDimItemModal = (event: any, name: any, id: any) => {
    setDeleteIdModal(id);
    setDeleteNameModal(name);
    setIsDeleteModal(true);
    event.stopPropagation();
  };

  const onDeleteModel = async () => {
    await dispatch(
      PostDeleteDimensionItem({
        ItemId: DeleteIdModal,
        parameters: { scenario_id: baseScenario?.id },
      }),
    );
    await dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
    setIsDeleteModal(false);
    setDeleteIdModal(null);
    setDeleteNameModal('');
    await dispatch(
      FetchBlockItemDimensions({
        blockId,
      }),
    );
    await dispatch(handleFilters());
  };

  // Memoize items array for better performance
  const items = useMemo(() => {
    return dataIndicators[dataKey?.name]?.items || [];
  }, [dataIndicators, dataKey?.name]);

  // Check if we should use virtualization
  const shouldVirtualize = items.length > VIRTUALIZATION_THRESHOLD;

  // Calculate list height dynamically based on container
  React.useEffect(() => {
    if (!shouldVirtualize) {
      return () => {
        // No cleanup needed if not virtualizing
      };
    }

    const updateHeight = () => {
      if (listContainerReference.current) {
        const height = listContainerReference.current.offsetHeight;
        if (height > 0) {
          setListHeight(height);
        }
      }
    };

    // Initial height calculation with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(updateHeight, 0);
    window.addEventListener('resize', updateHeight);

    // Use ResizeObserver if available for better performance
    let resizeObserver: ResizeObserver | null = null;
    if (listContainerReference.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(listContainerReference.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateHeight);
      if (resizeObserver && listContainerReference.current) {
        resizeObserver.unobserve(listContainerReference.current);
      }
    };
  }, [shouldVirtualize]);

  // Get item size for variable size list (expanded items are taller)
  const getItemSize = useCallback(
    (index: number) => {
      const item = items[index];
      const currentItemId = item?.id;
      const isExpanded = show === currentItemId;
      const baseHeight = 70; // Base height for collapsed item (includes margin)
      const gap = 10; // Gap between items

      if (!isExpanded) {
        const cachedHeight = itemHeightsReference.current[currentItemId];
        if (cachedHeight && cachedHeight <= baseHeight + gap) {
          return cachedHeight;
        }
        return baseHeight + gap;
      }

      // Check if we have a measured height from the DOM
      const measuredHeight = itemReferencesReference.current[currentItemId]?.offsetHeight;
      if (measuredHeight && measuredHeight > baseHeight) {
        const totalHeight = measuredHeight + gap;
        itemHeightsReference.current[currentItemId] = totalHeight;
        return totalHeight;
      }

      // Calculate expanded height based on actual content
      // DatePickerDrivers: ~60px
      // Each DesktopProperties: ~50px (includes border)
      // Box padding: 16px top + 16px bottom = 32px
      // Collapse animation buffer: ~30px
      const datePickerHeight = 60;
      const propertyHeight = 52; // Height per property (includes border)
      const boxPadding = 32; // Top and bottom padding (16px each)
      const collapseBuffer = 30; // Buffer for collapse animation and spacing
      const properties = getDisplayProperties(item?.properties || {});
      const propertyCount = Object.keys(properties).length;
      const propertiesHeight = propertyCount * propertyHeight;
      const calculatedExpandedHeight =
        datePickerHeight + propertiesHeight + boxPadding + collapseBuffer;

      // Minimum expanded height to ensure content is visible
      const minExpandedHeight = 250;
      const expandedHeight = Math.max(calculatedExpandedHeight, minExpandedHeight);

      // Cache the calculated height
      const totalHeight = baseHeight + expandedHeight + gap;
      itemHeightsReference.current[currentItemId] = totalHeight;

      return totalHeight;
    },
    [items, show],
  );

  // Reset item heights when items change
  React.useEffect(() => {
    itemHeightsReference.current = {};
    if (listReference.current) {
      listReference.current.resetAfterIndex(0);
    }
  }, [items.length]);

  // Update list when expansion state changes and scroll to expanded item
  React.useEffect(() => {
    if (listReference.current && shouldVirtualize) {
      // Reset heights for all items when expansion state changes
      listReference.current.resetAfterIndex(0, true);
      if (show) {
        // Find the index of the expanded item
        const expandedIndex = items.findIndex((item: any) => item.id === show);
        if (expandedIndex >= 0) {
          // Scroll to the expanded item after a delay to allow height recalculation
          setTimeout(() => {
            if (listReference.current) {
              // Use 'start' alignment to show item from top instead of centering
              listReference.current.scrollToItem(expandedIndex, 'start');
              // Reset again after scroll to ensure proper height
              setTimeout(() => {
                if (listReference.current) {
                  listReference.current.resetAfterIndex(expandedIndex, true);
                }
              }, 150);
            }
          }, 200);
        }
      }
    }
  }, [show, shouldVirtualize, items]);

  // Memoize itemData to ensure it updates when renameItemName changes
  const itemData = useMemo(() => {
    return {
      items,
      renameItemName,
      displayInfo,
      show,
    };
  }, [items, renameItemName, displayInfo, show]);

  // Reset list when renameItemName changes to trigger re-render
  React.useEffect(() => {
    if (listReference.current && shouldVirtualize && renameItemName) {
      const renamedIndex = items.findIndex((item: any) => item.id === renameItemName);
      if (renamedIndex >= 0) {
        listReference.current.resetAfterIndex(renamedIndex, true);
        // Scroll to renamed item from top
        setTimeout(() => {
          if (listReference.current) {
            listReference.current.scrollToItem(renamedIndex, 'start');
          }
        }, 100);
      }
    }
  }, [renameItemName, shouldVirtualize, items]);

  // Memoized component for rendering properties (only renders when expanded)
  const PropertiesList = React.memo(
    ({
      properties: itemProperties,
      elementId: itemElementId,
      onSubmitValue: handleSubmitValue,
      planPermission: itemPlanPermission,
      connectedDimensionItemsMap: itemConnectedDimensionItemsMap,
      timescale: itemTimescale,
    }: {
      properties: any;
      elementId: number;
      onSubmitValue: (value: string, id: number, propertyValue: string) => void;
      planPermission?: boolean;
      connectedDimensionItemsMap: Record<number, any[]>;
      timescale?: any;
    }) => {
      const displayProperties = useMemo(
        () => getDisplayProperties(itemProperties),
        [itemProperties],
      );

      return (
        <Box mb='15px' p='0 16px 16px 16px'>
          <DatePickerDrivers
            values={itemProperties}
            onSubmitValue={handleSubmitValue}
            id={itemElementId}
            timescale={itemTimescale}
          />
          {Object.entries(displayProperties).map(([keys, value]) => {
            return (
              <React.Fragment key={`${itemElementId}-${keys}`}>
                <DesktopProperties
                  keys={keys}
                  value={value}
                  onSubmitValue={handleSubmitValue}
                  dataElementsId={itemElementId}
                  planPermission={itemPlanPermission}
                  connectedDimensionItemsMap={itemConnectedDimensionItemsMap}
                />
              </React.Fragment>
            );
          })}
        </Box>
      );
    },
    (previousProperties, nextProperties) => {
      // Custom comparison function for better memoization
      return (
        previousProperties.elementId === nextProperties.elementId &&
        previousProperties.planPermission === nextProperties.planPermission &&
        JSON.stringify(previousProperties.properties) ===
          JSON.stringify(nextProperties.properties) &&
        JSON.stringify(previousProperties.connectedDimensionItemsMap) ===
          JSON.stringify(nextProperties.connectedDimensionItemsMap)
      );
    },
  );

  PropertiesList.displayName = 'PropertiesList';

  // Render a single item (used by both virtualized and non-virtualized lists)
  const renderItem = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (dataElements: any, index: number, provided?: any): any => {
      const elementId = dataElements.id;
      const draggableId: string = JSON.stringify({
        item_id: elementId,
        property_id: currentDriver?.group_property_id,
        value: dataElements?.name,
      });

      // Combined ref for both drag-and-drop and height measurement
      const combinedReference = (node: HTMLDivElement | null) => {
        if (shouldVirtualize) {
          itemReferencesReference.current[elementId] = node;
          // Measure height after render when expanded
          if (node && show === elementId) {
            // Clear any existing timeout for this item
            if (measurementTimeoutsReference.current[elementId]) {
              clearTimeout(measurementTimeoutsReference.current[elementId]);
            }
            // Only measure if we haven't already measured this height
            const cachedHeight = itemHeightsReference.current[elementId];
            measurementTimeoutsReference.current[elementId] = setTimeout(() => {
              const measuredHeight = node.offsetHeight;
              // Only update if height actually changed
              if (
                measuredHeight > 0 &&
                (!cachedHeight || Math.abs(cachedHeight - (measuredHeight + 10)) > 5)
              ) {
                itemHeightsReference.current[elementId] = measuredHeight + 10; // Add gap
                if (listReference.current) {
                  listReference.current.resetAfterIndex(index, true);
                }
              }
              // Clear the timeout reference after measurement
              delete measurementTimeoutsReference.current[elementId];
            }, 300); // Wait for collapse animation
          } else if (measurementTimeoutsReference.current[elementId]) {
            // Clear timeout if item is not expanded
            clearTimeout(measurementTimeoutsReference.current[elementId]);
            delete measurementTimeoutsReference.current[elementId];
          }
        }
        if (provided?.innerRef) {
          if (typeof provided.innerRef === 'function') {
            provided.innerRef(node);
          } else {
            provided.innerRef.current = node;
          }
        }
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // @ts-ignore - Complex union type from Chakra UI and drag-and-drop props
      const itemContent: any = (
        <Flex
          ref={combinedReference}
          {...(provided?.draggableProps || {})}
          {...(provided?.dragHandleProps || {})}
          key={index}
          textAlign='left'
          alignSelf='left'
          w='100%'
          minH='50px'
          bgColor='#7163D0'
          mb={shouldVirtualize ? '0' : '10px'}
          mt={shouldVirtualize ? '10px' : '0'}
          p='0'
          flexDirection='column'
          borderRadius='14px'
          filter='drop-shadow(0px 0px 15.998px rgba(0, 0, 0, 0.25))'
          transition='all 0.3s ease-out'
          zIndex={show === elementId || displayInfo === elementId ? '2' : '0'}
          css={styles.container(show === elementId || displayInfo === elementId)}
        >
          <Box
            onClick={(event) => {
              // Don't handle click if it's from a menu item
              const target = event.target as HTMLElement;
              const isMenuClick =
                target.closest('[role="menuitem"]') ||
                target.closest('[data-menu]') ||
                target.closest('button[aria-haspopup="menu"]');
              if (isMenuClick) {
                return;
              }
              showOrRename(event, elementId);
              dispatch(setSelectedElement(_index));
              dispatch(setDisplayInfo(null));
            }}
            position='relative'
            p='16px'
            onBlur={() => {
              const currentValue = itemNameField[elementId] || '';
              const originalValue = dataElements?.name || '';
              // Only call API if user has actually typed something and value has changed
              if (
                hasUserTypedReference.current[elementId] &&
                currentValue &&
                currentValue.trim() &&
                currentValue.trim() !== originalValue.trim() &&
                renameItemName === elementId
              ) {
                debouncedUpdateItemApi(elementId, currentValue);
              }
              // Reset the flag when blur happens
              hasUserTypedReference.current[elementId] = false;
            }}
          >
            {/* @ts-ignore - Complex union type from Chakra UI props */}
            <Flex justifyContent='space-between' className='infoButtonParent' {...longClickCheck()}>
              {renameItemName === elementId && !planPermission ? (
                <Flex alignItems='center' gap='8px' width='100%'>
                  <InputField
                    width={'fit-content'}
                    outline={'none !important'}
                    boxShadow={'none !important'}
                    border={'1px white solid !important'}
                    padding={'15px 5px 15px 10px'}
                    height={'28px'}
                    fontSize={'16px !important'}
                    fontFamily={'Roboto Bold'}
                    value={itemNameField[elementId] ?? dataElements?.name ?? ''}
                    onFocus={() => {
                      // Input focused
                    }}
                    onBlur={() => {
                      // Input blurred
                    }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = event.target;
                      // Mark that user has typed something
                      hasUserTypedReference.current[elementId] = true;
                      setItemNameField((previous) => ({
                        ...previous,
                        [elementId]: value,
                      }));
                    }}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      const target = event.target as HTMLInputElement;
                      const { value } = target;
                      if (value.trim().length > 0) {
                        onKeyPressAction(event, elementId);
                      }
                    }}
                    onKeyUp={() => {
                      // Key up handler
                    }}
                    _placeholder={{ color: '#ffffff66' }}
                    autoFocus
                    isDisabled={isRenaming[elementId]}
                  />
                  {isRenaming[elementId] && (
                    <Spinner
                      size='sm'
                      thickness='2px'
                      styleProperties={{
                        color: 'white',
                      }}
                    />
                  )}
                  {renameSuccess[elementId] && !isRenaming[elementId] && (
                    <Text color='green.300' fontSize='xs'>
                      ✓
                    </Text>
                  )}
                </Flex>
              ) : (
                <Tooltip label={dataElements?.name}>
                  <Text
                    fontFamily='Roboto Bold'
                    fontSize='sx'
                    lineHeight='9'
                    fontWeight='bold'
                    padding={'2px'}
                    noOfLines={isLongClicked ? 0 : 1}
                  >
                    {dataElements?.name}
                  </Text>
                </Tooltip>
              )}
              <Flex alignItems='center' position='relative' ref={reference}>
                <Menu
                  isOpen={displayInfo === elementId}
                  closeOnSelect={false}
                  strategy='fixed'
                  isLazy
                  matchWidth={false}
                  onClose={() => {
                    dispatch(setDisplayInfo(null));
                  }}
                >
                  {({ isOpen }) => (
                    <>
                      <MenuButton
                        width={'auto'}
                        marginRight='1rem'
                        cursor={planPermission ? 'not-allowed' : 'pointer'}
                        onClick={
                          planPermission
                            ? undefined
                            : (event) => {
                                event.stopPropagation();
                                toggleInfoPopup(dataElements?.id);
                              }
                        }
                      >
                        <Image src={infoIcon} w='28px' height='18px' className='infoButton' />
                      </MenuButton>
                      {isOpen && (
                        <Portal>
                          <MenuList
                            zIndex='10000'
                            position='fixed'
                            onMouseDown={(event) => {
                              const target = event.target as HTMLElement;
                              const menuItem = target.closest('[role="menuitem"]');
                              const menuItemText = menuItem?.textContent?.trim();
                              // Handle rename if clicking on Rename menu item
                              if (menuItemText === 'Rename') {
                                event.stopPropagation();
                                // Initialize the input value with the current item name
                                setItemNameField((previous) => ({
                                  ...previous,
                                  [elementId]: dataElements?.name || '',
                                }));
                                // Reset the hasUserTyped flag when starting rename
                                hasUserTypedReference.current[elementId] = false;
                                dispatch(setRenameItemName(elementId));
                                dispatch(setSelectedElement(_index));
                                dispatch(setDisplayInfo(null));
                              }
                              // Handle delete if clicking on Delete menu item
                              else if (menuItemText === 'Delete') {
                                event.stopPropagation();
                                DeleteDimItemModal(event, dataElements?.name, elementId);
                              }
                            }}
                          >
                            <MenuItem padding='.5rem 1.5rem'>Rename</MenuItem>
                            <MenuItem padding='.5rem 1.5rem'>Delete</MenuItem>
                          </MenuList>
                        </Portal>
                      )}
                    </>
                  )}
                </Menu>
                <Image src={show === elementId ? arrowUp : arrowDown} w='12px' />
              </Flex>
            </Flex>
          </Box>
          {show === elementId && (
            <Collapse isOpened={true} width='100%'>
              <PropertiesList
                properties={dataElements?.properties}
                elementId={elementId}
                onSubmitValue={onSubmitValue}
                planPermission={planPermission}
                connectedDimensionItemsMap={connectedDimensionItemsMap}
                timescale={dataElements?.timescale}
              />
            </Collapse>
          )}
        </Flex>
      );

      // Wrap with Draggable only if not virtualized (drag and drop doesn't work well with virtualization)
      if (!shouldVirtualize && provided) {
        return (
          <React.Fragment key={index}>
            <Draggable key={`${elementId}`} draggableId={draggableId} index={index}>
              {() => itemContent}
            </Draggable>
          </React.Fragment>
        );
      }

      return <React.Fragment key={index}>{itemContent}</React.Fragment>;
    },
    [
      currentDriver?.group_property_id,
      show,
      displayInfo,
      itemNameField,
      renameItemName,
      planPermission,
      isLongClicked,
      isRenaming,
      renameSuccess,
      dataKey?.name,
      onSubmitValue,
      connectedDimensionItemsMap,
      styles,
      showOrRename,
      _index,
      dispatch,
      debouncedUpdateItemApi,
      onKeyPressAction,
      longClickCheck,
      toggleInfoPopup,
      DeleteDimItemModal,
      getDisplayProperties,
      shouldVirtualize,
    ],
  );

  // Virtualized Row component
  const VirtualizedRow = React.memo(
    ({ index, style, data }: any) => {
      const item = data.items[index];
      return (
        <Box
          style={{
            ...style,
            paddingRight: '10px',
            paddingLeft: '10px',
            paddingTop: index === 0 ? '10px' : '0',
            paddingBottom: '0',
          }}
        >
          {renderItem(item, index)}
        </Box>
      );
    },
    (previousProperties, nextProperties) => {
      // Custom comparison to re-render when renameItemName or other state changes
      const shouldSkipRender =
        previousProperties.data.items === nextProperties.data.items &&
        previousProperties.data.renameItemName === nextProperties.data.renameItemName &&
        previousProperties.data.displayInfo === nextProperties.data.displayInfo &&
        previousProperties.data.show === nextProperties.data.show &&
        previousProperties.index === nextProperties.index &&
        previousProperties.style === nextProperties.style;

      return shouldSkipRender;
    },
  );

  VirtualizedRow.displayName = 'VirtualizedRow';

  return (
    <>
      {shouldVirtualize ? (
        <Box
          ref={listContainerReference}
          width='100%'
          height='calc(100vh - 300px)'
          overflow='hidden'
          style={{ position: 'relative' }}
        >
          <List
            ref={listReference}
            height={listHeight}
            itemCount={items.length}
            itemSize={getItemSize}
            itemData={itemData}
            width='100%'
            overscanCount={5}
          >
            {VirtualizedRow}
          </List>
        </Box>
      ) : (
        items.map((dataElements: any, index: number) => {
          const draggableId: string = JSON.stringify({
            item_id: dataElements.id,
            property_id: currentDriver?.group_property_id,
            value: dataElements?.name,
          });
          return (
            <React.Fragment key={index}>
              <Draggable key={`${dataElements.id}`} draggableId={draggableId} index={index}>
                {(provided: any) => renderItem(dataElements, index, provided)}
              </Draggable>
            </React.Fragment>
          );
        })
      )}
      <DeleteModal
        isOpen={isDeleteModal}
        onClose={() => setIsDeleteModal(false)}
        itemName={DeleteNameModal}
        onDelete={onDeleteModel}
      />
    </>
  );
};

export default DesktopElements;
